import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Section } from "./Common";
import PractitionerRecommendations from "./PractitionerRecommendations";
import BRAND from "../constants/brand";
import { ArrowRight, Sparkles, Quote, BookOpen, ShoppingBag } from "lucide-react";

/**
 * Reusable premium section page for ecosystem topics.
 *
 * Phase 6.3 — DEEP CATEGORY CONTENT ARCHITECTURE
 * -----------------------------------------------
 * Now supports:
 *   - Hero (image + eyebrow + title + subtitle + 2 CTAs)
 *   - Topic Pillars grid
 *   - Editorial body sections [{title, body}]   (new — long-form)
 *   - Quotes / mantras strip [{text, author?}]   (new — ambience)
 *   - Curated articles / reads [{title, summary?, to?, image?}] (new)
 *   - Recommended products [{title, kind, to?, image?}] (new)
 *   - Practitioner recommendations (Marketplace)
 *   - Ask Donny premium block (always — page-aware via global drawer)
 *   - Related ecosystem links
 *
 * Backward-compatible: pages that don't supply the new fields still render.
 */
export default function EcosystemTopicPage({
  eyebrow,
  title,
  subtitle,
  image,
  pillars = [],
  editorial = [],
  quotes = [],
  reads = [],
  products = [],
  relatedLinks = [],
  marketplaceTopic,
  marketplaceTitle,
  askDonnyPrompt,
  testid = "ecosystem-topic",
}) {
  const { t } = useTranslation();
  // The deep-link still works for `/ask-donny?prompt=...`; the global
  // floating drawer is the primary entry point and is page-aware automatically.
  const askDonnyHref = askDonnyPrompt
    ? `/ask-donny?prompt=${encodeURIComponent(askDonnyPrompt)}`
    : "/ask-donny";

  return (
    <div data-testid={testid}>
      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden border-b border-white/10">
        {image && (
          <div className="absolute inset-0">
            <img src={image} alt="" className="w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/25" />
          </div>
        )}
        <div className="relative app-container py-20 md:py-28">
          <div className="max-w-2xl">
            {eyebrow && <div className="eyebrow mb-3">{eyebrow}</div>}
            <h1 className="font-display text-4xl md:text-5xl leading-tight max-w-[20ch]" data-testid={`${testid}-title`}>
              {title}
            </h1>
            {subtitle && (
              <p className="text-white/82 mt-5 leading-relaxed max-w-[60ch] body-copy text-lg">
                {subtitle}
              </p>
            )}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={askDonnyHref}
                className="btn-primary text-sm inline-flex items-center gap-2"
                data-testid={`${testid}-ask-donny`}
              >
                <Sparkles className="w-4 h-4" /> {t("nav.ask_donny")}
              </Link>
              <Link to="/dosha" className="btn-ghost text-sm" data-testid={`${testid}-dosha`}>
                {t("dosha.cta_short")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TOPIC PILLARS ===================== */}
      {pillars.length > 0 && (
        <Section
          eyebrow={t("ecosystem.pillars_eyebrow", "— What we cover")}
          title={t("ecosystem.pillars_title", "Topics in this section")}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testid}-pillars`}>
            {pillars.map((p, i) => (
              <div
                key={i}
                className="card-surface p-5 hover:border-[#C8A24A]/40 transition-colors"
                data-testid={`${testid}-pillar-${i}`}
              >
                <div className="font-display text-lg mb-2">{p.title}</div>
                <p className="text-sm text-white/72 leading-relaxed">{p.body}</p>
                {p.cta && (
                  <Link
                    to={p.cta.to}
                    className="text-xs gold-text inline-flex items-center gap-1 mt-3 hover:underline"
                  >
                    {p.cta.label} <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ===================== EDITORIAL — long-form body sections ===================== */}
      {editorial.length > 0 && (
        <Section
          eyebrow={t("ecosystem.editorial_eyebrow", "— Editorial")}
          title={t("ecosystem.editorial_title", "Go deeper")}
        >
          <div className="grid md:grid-cols-2 gap-5" data-testid={`${testid}-editorial`}>
            {editorial.map((e, i) => (
              <article
                key={i}
                className="card-surface p-6 md:p-7"
                data-testid={`${testid}-editorial-${i}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#E2C77A]/25 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center shrink-0">
                    <BookOpen className="w-4 h-4 gold-text" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl leading-tight">{e.title}</h3>
                </div>
                <div className="prose-coach text-sm md:text-[15px] text-white/80 leading-relaxed whitespace-pre-line">
                  {e.body}
                </div>
              </article>
            ))}
          </div>
        </Section>
      )}

      {/* ===================== QUOTES / MANTRAS STRIP ===================== */}
      {quotes.length > 0 && (
        <Section
          eyebrow={t("ecosystem.quotes_eyebrow", "— Quotes & Mantras")}
          title={t("ecosystem.quotes_title", "Sit with this")}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testid}-quotes`}>
            {quotes.map((q, i) => (
              <figure
                key={i}
                className="card-surface p-6 relative overflow-hidden"
                data-testid={`${testid}-quote-${i}`}
              >
                <Quote className="absolute top-3 right-3 w-7 h-7 gold-text opacity-25" />
                <blockquote className="font-display text-lg md:text-xl leading-snug text-white/90">
                  &ldquo;{q.text}&rdquo;
                </blockquote>
                {q.author && (
                  <figcaption className="mt-3 text-xs tracking-[0.2em] uppercase gold-text">
                    — {q.author}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </Section>
      )}

      {/* ===================== CURATED READS / ARTICLES ===================== */}
      {reads.length > 0 && (
        <Section
          eyebrow={t("ecosystem.reads_eyebrow", "— Curated Reads")}
          title={t("ecosystem.reads_title", "What to read next")}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testid}-reads`}>
            {reads.map((r, i) => {
              const Wrapper = r.to ? Link : "div";
              const wrapProps = r.to
                ? { to: r.to, className: "card-surface overflow-hidden group hover:border-[#C8A24A]/45 transition-colors flex flex-col" }
                : { className: "card-surface overflow-hidden flex flex-col" };
              return (
                <Wrapper key={i} {...wrapProps} data-testid={`${testid}-read-${i}`}>
                  {r.image ? (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
                    </div>
                  ) : (
                    <div className="aspect-[16/6] bg-gradient-to-br from-[#15161D] via-[#0F1116] to-[#0A0A0F] flex items-center justify-center">
                      <BookOpen className="w-6 h-6 gold-text opacity-60" />
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    {r.tag && <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-2">{r.tag}</div>}
                    <div className="font-display text-lg leading-snug max-w-[28ch]">{r.title}</div>
                    {r.summary && <div className="text-sm text-white/65 mt-2 line-clamp-3 leading-relaxed">{r.summary}</div>}
                    {r.to && (
                      <div className="mt-3 text-xs gold-text inline-flex items-center gap-1">
                        {t("ecosystem.read")} <ArrowRight className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                </Wrapper>
              );
            })}
          </div>
        </Section>
      )}

      {/* ===================== RECOMMENDED PRODUCTS ===================== */}
      {products.length > 0 && (
        <Section
          eyebrow={t("ecosystem.products_eyebrow", "— From the Shop")}
          title={t("ecosystem.products_title", "Recommended products")}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testid}-products`}>
            {products.map((p, i) => {
              const href = p.to || "/shop";
              return (
                <Link
                  key={i}
                  to={href}
                  className="card-surface p-5 group hover:border-[#C8A24A]/45 transition-colors flex flex-col"
                  data-testid={`${testid}-product-${i}`}
                >
                  <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#E2C77A]/30 to-[#7A5F2A]/10 border border-[#C8A24A]/30 grid place-items-center mb-3">
                    <ShoppingBag className="w-4 h-4 gold-text" />
                  </div>
                  <div className="text-[10px] tracking-[0.28em] uppercase gold-text">{p.kind || t("ecosystem.program")}</div>
                  <div className="font-display text-lg leading-tight mt-1">{p.title}</div>
                  {p.summary && <p className="text-sm text-white/65 mt-2 leading-relaxed line-clamp-3">{p.summary}</p>}
                  <div className="mt-3 text-xs gold-text inline-flex items-center gap-1 group-hover:underline">
                    {t("ecosystem.explore")} <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </Section>
      )}

      {/* ===================== ASK DONNY — premium block ===================== */}
      <Section eyebrow={t("ecosystem.donny_eyebrow")} title={t("ecosystem.donny_about", { title })}>
        <div
          className="card-surface p-6 md:p-8 grid md:grid-cols-[140px_1fr_auto] items-center gap-6"
          data-testid={`${testid}-donny-block`}
        >
          <img
            src={BRAND.DONNY_AVATAR}
            alt={t("ecosystem.donny_avatar_alt")}
            className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover ring-1 ring-[#C8A24A]/35 shadow-[0_0_40px_rgba(212,175,55,0.25)] mx-auto md:mx-0"
            loading="lazy"
          />
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-2 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> {t("ecosystem.donny_meta_chip")}
            </div>
            <h3 className="font-display text-2xl md:text-3xl leading-tight">
              {t("ecosystem.donny_hl")}{" "}
              <span className="gold-text">{title}</span>.
            </h3>
            <p className="text-white/72 mt-2 max-w-[60ch] leading-relaxed body-copy">
              {askDonnyPrompt
                ? t("ecosystem.donny_try", { prompt: askDonnyPrompt })
                : t("ecosystem.donny_default_body")}
            </p>
          </div>
          <Link
            to={askDonnyHref}
            className="btn-primary text-sm inline-flex items-center gap-2 whitespace-nowrap self-start md:self-auto"
            data-testid={`${testid}-donny-cta`}
          >
            <Sparkles className="w-4 h-4" /> {t("nav.ask_donny")}
          </Link>
        </div>
      </Section>

      {/* ===================== PRACTITIONERS ===================== */}
      {marketplaceTopic && (
        <PractitionerRecommendations
          eyebrow={t("marketplace.find_pro_eyebrow", "— Find a Pro")}
          title={marketplaceTitle || t("marketplace.find_pro_default", "Find a practitioner")}
          topic={marketplaceTopic}
          limit={3}
          testid={`${testid}-practitioners`}
        />
      )}

      {/* ===================== RELATED ECOSYSTEM LINKS ===================== */}
      {relatedLinks.length > 0 && (
        <Section
          eyebrow={t("ecosystem.related_eyebrow", "— Continue exploring")}
          title={t("ecosystem.related_title", "Related in the ecosystem")}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`${testid}-related`}>
            {relatedLinks.map((l, i) => (
              <Link
                key={i}
                to={l.to}
                className="card-surface p-4 group hover:border-[#C8A24A]/40 transition-colors flex items-center justify-between gap-3"
                data-testid={`${testid}-related-${i}`}
              >
                <div>
                  <div className="font-display text-base group-hover:gold-text transition-colors">{l.label}</div>
                  {l.hint && <div className="text-xs text-white/55 mt-1">{l.hint}</div>}
                </div>
                <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] transition-colors" />
              </Link>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
